import { useRender } from '@base-ui/react';
import { cva, type VariantProps } from 'class-variance-authority';
import type { MouseEvent, ReactElement } from 'react';
import { Container } from '../containers/container.tsx';
import { cn } from '../util/classes.ts';
import { PolymorphicButtonBase, type PolymorphicButtonBaseProps } from './button.tsx';

export type InputGroupProps = useRender.ComponentProps<'div'>;

export function InputGroup({ className, render, ...props }: InputGroupProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        props: {
            className: cn(
                'group/input-group relative flex min-h-interactive w-full min-w-0',
                'items-center rounded-tight border border-line outline-none',
                'transition duration-state ease-state',
                'in-data-[slot=combobox-content]:focus-within:border-inherit',
                'in-data-[slot=combobox-content]:focus-within:ring-0',
                'has-[[data-slot=input-group-control]:focus-visible]:border-brand',
                'has-[[data-slot=input-group-control]:focus-visible]:ring-1',
                'has-[[data-slot=input-group-control]:focus-visible]:ring-brand/(--opacity-wash)',
                'state-invalid-within:text-danger state-invalid-within:border-danger',
                'state-invalid-within:has-[[data-slot=input-group-control]:focus-visible]:border-danger',
                'state-invalid-within:has-[[data-slot=input-group-control]:focus-visible]:ring-danger/(--opacity-wash)',
                'state-disabled-within:cursor-not-allowed state-disabled-within:border-line-weak',
                'state-disabled-within:bg-container-disabled state-disabled-within:text-content-disabled',
                'state-disabled-within:placeholder:text-transparent',
                'has-[>[data-align=block-end]]:h-auto',
                'has-[>[data-align=block-end]]:flex-col',
                'has-[>[data-align=block-start]]:h-auto',
                'has-[>[data-align=block-start]]:flex-col',
                'has-[>textarea]:h-auto',
                className,
            ),
            role: 'group',
            'data-slot': 'input-group',
            ...props,
        },
        render,
    });
}

const inputGroupAddonVariants = cva(
    [
        'flex h-auto cursor-text items-center justify-center gap-tight py-tight',
        'text-ui-sm font-medium text-content-weak select-none',
        'group-data-[disabled=true]/input-group:opacity-disabled',
        '[&>kbd]:rounded-tight',
        '[&>svg:not([class*="size-"])]:size-4',
    ],
    {
        variants: {
            align: {
                'inline-start': ['order-first pl-cozy'],
                'inline-end': ['order-last pr-cozy'],
                'block-start': ['order-first w-full justify-start', 'px-cozy pt-cozy'],
                'block-end': ['order-last w-full justify-start', 'px-default pb-cozy'],
            },
        },
        defaultVariants: {
            align: 'inline-start',
        },
    },
);

export type InputGroupAddonProps = useRender.ComponentProps<'div'> &
    VariantProps<typeof inputGroupAddonVariants>;

export function InputGroupAddon({
    className,
    align = 'inline-start',
    render,
    ...props
}: InputGroupAddonProps): ReactElement {
    const element = useRender({
        defaultTagName: 'div',
        props: {
            className: cn(inputGroupAddonVariants({ align }), className),
            role: 'group',
            'data-slot': 'input-group-addon',
            'data-align': align,
            render,
            onClick: (e: MouseEvent<HTMLDivElement>) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                if ((e.target as HTMLElement).closest('button')) {
                    return;
                }
                e.currentTarget.parentElement?.querySelector('input')?.focus();
            },
            ...props,
        },
    });
    return <Container render={element} minLevel={2} />;
}

export type InputGroupButtonProps = PolymorphicButtonBaseProps;

export function InputGroupButton({ className, ...props }: InputGroupButtonProps): ReactElement {
    return (
        <PolymorphicButtonBase
            className={cn(
                'rounded-tight p-tight cursor-pointer',
                'hover:bg-content/(--opacity-wash) hover:text-content',
                className,
            )}
            {...props}
        />
    );
}

export type InputGroupTextProps = useRender.ComponentProps<'span'>;

export function InputGroupText({ className, ...props }: InputGroupTextProps): ReactElement {
    return (
        <span
            className={cn(
                'flex items-center gap-cozy',
                'text-ui-sm text-content-weak',
                '[&_svg]:pointer-events-none',
                '[&_svg:not([class*="size-"])]:size-4',
                className,
            )}
            {...props}
        />
    );
}

export type InputGroupInputProps = React.ComponentProps<'input'>;

export function InputGroupInput({ className, ...props }: InputGroupInputProps): ReactElement {
    return (
        <input
            data-slot="input-group-control"
            className={cn(
                'min-h-interactive px-cozy w-full',
                'flex-1 rounded-none border-0 bg-transparent shadow-none ring-0',
                'focus-visible:ring-0 focus-visible:outline-none',
                'disabled:bg-transparent',
                'state-invalid:border-none',
                className,
            )}
            {...props}
        />
    );
}

export type InputGroupTextareaProps = React.ComponentProps<'textarea'>;

export function InputGroupTextarea({ className, ...props }: InputGroupTextareaProps): ReactElement {
    return (
        <textarea
            data-slot="input-group-control"
            className={cn(
                'min-h-interactive px-cozy w-full',
                'flex-1 rounded-none border-0 bg-transparent shadow-none ring-0',
                'focus-visible:ring-0 focus-visible:outline-none',
                'disabled:bg-transparent',
                'state-invalid:border-none',
                'resize-none py-2',
                className,
            )}
            {...props}
        />
    );
}

// 'flex-1 rounded-none border-0 bg-transparent shadow-none ring-0',
// 'focus-visible:ring-0',
// 'disabled:bg-transparent',
// 'state-invalid:border-none',
// 'resize-none py-2',
